import {
  AppShell,
  type MenuSection,
  type NavigationItem,
  type ShellTheme,
} from '@starwardforge/web-kit';
import { BookOpen, Gauge, Network, ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';

import { apiChangelogUrl, developerDocumentationUrl } from './documentation';

export type ConnectPlaneShellSurface = 'marketing' | 'user-docs' | 'developer-docs';

type ConnectPlaneShellProps = {
  surface: ConnectPlaneShellSurface;
  sectionLabel: string;
  brandHref: string;
  brandLabel: string;
  children: ReactNode;
  activeItemId?: string;
  topbarActions?: ReactNode;
  footer?: ReactNode;
  mainContentId: string;
  dedicatedDocsHost?: boolean;
  theme?: ShellTheme;
};

function globalShellDestinations(_dedicatedDocsHost: boolean) {
  const hostname = window.location.hostname.toLowerCase();
  const siteOrigin = '';

  return {
    apiChangelogHref: apiChangelogUrl(hostname),
    developerDocsHref: developerDocumentationUrl(hostname),
    portalChangelogHref: `${siteOrigin}/docs/changelog`,
    signInHref: import.meta.env.VITE_PORTAL_SIGN_IN_URL || 'https://myaccount.connectplane.com/',
    siteOrigin,
    userDocsHref: `${siteOrigin}/docs`,
  };
}

function globalNavigation(dedicatedDocsHost: boolean): readonly NavigationItem[] {
  const destinations = globalShellDestinations(dedicatedDocsHost);
  return [
    { id: 'platform', label: 'Platform', href: `${destinations.siteOrigin}/#platform`, icon: <Network aria-hidden="true" />, keywords: ['capabilities', 'service lifecycle'] },
    { id: 'outcomes', label: 'Why ConnectPlane', href: `${destinations.siteOrigin}/#outcomes`, icon: <Gauge aria-hidden="true" />, keywords: ['outcomes', 'benefits'] },
    { id: 'trust', label: 'Trust', href: `${destinations.siteOrigin}/#trust`, icon: <ShieldCheck aria-hidden="true" />, keywords: ['security', 'identity'] },
    { id: 'user-guides', label: 'User guides', href: destinations.userDocsHref, icon: <BookOpen aria-hidden="true" />, keywords: ['help', 'concepts', 'documentation'] },
  ];
}

function globalMenuSections(dedicatedDocsHost: boolean): readonly MenuSection[] {
  const destinations = globalShellDestinations(dedicatedDocsHost);
  return [
    {
      label: 'Documentation, updates, and account',
      items: [
        { id: 'developer-docs', label: 'Developer documentation ↗', href: destinations.developerDocsHref, keywords: ['API', 'SDK', 'reference', 'external'] },
        { id: 'portal-updates', label: 'Portal changelog', href: destinations.portalChangelogHref },
        { id: 'api-updates', label: 'API changelog', href: destinations.apiChangelogHref },
        { id: 'sign-in', label: 'Sign in', href: destinations.signInHref },
      ],
    },
  ];
}

export function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}

export function ConnectPlaneBrand({ sectionLabel }: { sectionLabel: string }) {
  return (
    <span className="connectplane-shell-brand">
      <BrandMark />
      <span><strong>ConnectPlane</strong><small>{sectionLabel}</small></span>
    </span>
  );
}

export function ConnectPlaneShell({
  surface,
  sectionLabel,
  brandHref,
  brandLabel,
  children,
  activeItemId,
  topbarActions,
  footer,
  mainContentId,
  dedicatedDocsHost = false,
  theme = 'light',
}: ConnectPlaneShellProps) {
  const navigation = globalNavigation(dedicatedDocsHost);
  const menuSections = globalMenuSections(dedicatedDocsHost);

  return (
    <div className={`connectplane-shared-shell connectplane-shared-shell--${surface}`}>
      <AppShell
        theme={theme}
        brand={<ConnectPlaneBrand sectionLabel={sectionLabel} />}
        brandHref={brandHref}
        brandLabel={brandLabel}
        navigation={navigation}
        activeItemId={activeItemId}
        topbarActions={topbarActions}
        menuSections={menuSections}
        footer={footer}
        mainContentId={mainContentId}
        navigationLabel="ConnectPlane navigation"
        menuTitle="ConnectPlane menu"
        menuSearchLabel="Search destinations"
      >
        {children}
      </AppShell>
    </div>
  );
}
